import { Component } from '@theme/component';

/**
 * Avis compacts — carrousel en scroll natif.
 *
 * Le suivi de la carte visible se fait par un écouteur scroll direct et un simple
 * calcul de position : l'événement scroll ne remonte pas dans le DOM (la délégation
 * d'Horizon ne le voit pas), et IntersectionObserver est suspendu dans les onglets
 * en arrière-plan — le calcul direct, lui, marche partout.
 */
export class ReviewsCompact extends Component {
  requiredRefs = ['track'];

  /** @type {HTMLElement[]} */
  #dots = [];

  connectedCallback() {
    super.connectedCallback();

    const { track, cards } = this.refs;
    if (!cards?.length) return;

    // Version bloc : le conteneur de puces arrive vide, on les construit ici.
    this.#dots = this.refs.dotList ?? [];
    if (!this.#dots.length && this.refs.dots) {
      cards.forEach((card, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'rvc__dot';
        dot.setAttribute('aria-label', `Avis ${i + 1}`);
        dot.addEventListener('click', () => this.#scrollTo(i));
        this.refs.dots.appendChild(dot);
        this.#dots.push(dot);
      });
    }

    track.addEventListener('scroll', () => this.#paint(), { passive: true });
    this.#paint();
  }

  get #index() {
    const { track, cards } = this.refs;
    if (!cards?.length) return 0;
    const step = cards[0].offsetWidth + this.#gap();
    return Math.min(cards.length - 1, Math.max(0, Math.round(track.scrollLeft / step)));
  }

  #gap() {
    const value = parseFloat(getComputedStyle(this.refs.track).columnGap);
    return Number.isNaN(value) ? 0 : value;
  }

  #paint() {
    if (!this.#dots.length) return;
    const index = this.#index;
    this.#dots.forEach((dot, i) => dot.classList.toggle('rvc__dot--active', i === index));
  }

  /** Appelé par `on:click="/goTo"` sur les puces rendues par la section. */
  goTo(event) {
    this.#scrollTo(Number(event.target.dataset.index) || 0);
  }

  next() {
    this.#scrollTo(this.#index + 1);
  }

  prev() {
    this.#scrollTo(this.#index - 1);
  }

  #scrollTo(index) {
    const { track, cards } = this.refs;
    if (!cards?.length) return;
    const total = cards.length;
    const target = ((index % total) + total) % total;
    track.scrollTo({ left: target * (cards[0].offsetWidth + this.#gap()), behavior: 'smooth' });
  }
}

if (!customElements.get('reviews-compact')) {
  customElements.define('reviews-compact', ReviewsCompact);
}
